/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ServiceLinesState } from 'Types/GlobalTypes';
import { AddToFavoriteRequest, GetCompaniesRequest } from 'Types/RequestBodies';
import {
  ServiceLine, SLEditCompanyRequest, SLDetailResponse, SLNewsRequest, SLResponse, SLWidgetResponse,
  SLRegionsResponse,
  RegionsResponse,
} from 'Types/ServiceLinesTypes';
import { mergeFilters } from 'Utils/UtilityFunctions';
import { SRIFilters, SRIFiltersRequest } from '../Types/SRITypes';
import { CompaniesDataResponse } from '../Types/CompaniesTypes';

export const initialState: ServiceLinesState = {
  followedServiceLines: [],
  serviceLinesEmpty: false,
  loading: false,
  recommendedServiceLines: [],
  followLoading: false,

  detailLoading: false,
  newsArticlesLoading: false,
  newsTabPage: 1,
  newsArticles: [],
  articleNewsEnded: false,
  newsFilter: [],
  selectedNewsFilter: [],

  widgetLoading: false,
  serviceLineWidgets: [],

  servicelineName: '',
  paginationLoader: false,
  servicelineAdded: false,
  filters: {
    start_date: '',
    end_date: '',
    allTime: true,
  } as SRIFilters,
  filtersType: 'All Time',
  companiesLoading: false,
  companies: [],
  companiesCount: 0,
  companyAdded: false,
  pendingWidgetDetails: null,
  regionsLoading: false,
  regions: [],
  selectedRegions: [],
};

const ServiceLinesSlice = createSlice({
  name: 'serviceline',
  initialState,
  reducers: {
    getServiceLines(state) {
      state.loading = true;
      state.serviceLinesEmpty = false;
    },
    setServiceLines(state, action: PayloadAction<SLResponse>) {
      const { payload } = action;
      state.loading = false;
      state.recommendedServiceLines = payload.recommendedServiceLines;
      state.followedServiceLines = payload.followedServiceLines;
      if (state.followedServiceLines.length < 1) state.serviceLinesEmpty = true;
    },
    removeServiceLines(state, action: PayloadAction<ServiceLine[]>) {
      state.loading = true;
    },
    removedtServiceLines(state, action: PayloadAction<ServiceLine[]>) {
      state.loading = false;
      state.followedServiceLines = state.followedServiceLines.filter(
        (ind) => !action.payload.find(({ id }) => ind.id === id),
      );
      const unfollowedServiceLine = action.payload.map(
        (serviceLine) => ({ ...serviceLine, added: false }),
      );
      state.recommendedServiceLines = [...unfollowedServiceLine, ...state.recommendedServiceLines];
      if (state.followedServiceLines.length < 1) {
        state.serviceLinesEmpty = true;
      }
    },
    addingServiceLine(state, action: PayloadAction<ServiceLine>) {
      if (action.payload.type === 'detailPage') {
        // Reset any previous loading state to prevent stuck states
        state.followLoading = false;
        state.followLoading = true;
      } else {
        const index = state.recommendedServiceLines.findIndex(
          (v) => v.id === action.payload.id,
        );
        if (index > -1) {
          // Only set loading if not already loading to prevent multiple rapid clicks
          if (!state.recommendedServiceLines[index].loading) {
            state.recommendedServiceLines[index].loading = true;
          }
        }
      }
    },
    addedServiceLineDetail(state, action: PayloadAction<ServiceLine>) {
      state.servicelineAdded = true;
      const temp = state.recommendedServiceLines.find(
        (v) => v.id === action.payload.id,
      );
      if (temp) {
        temp.added = true;
        state.followedServiceLines.unshift(temp);
      }
      state.followLoading = false;

      state.recommendedServiceLines = state.recommendedServiceLines.filter(
        (ind) => action.payload.id !== ind.id,
      );
      if (state.followedServiceLines.length >= 1) {
        state.serviceLinesEmpty = false;
      }
    },
    addedServiceLine(state, action: PayloadAction<ServiceLine>) {
      const { id } = action.payload;

      // Check if item is already in followed list to prevent duplicates
      const alreadyFollowed = state.followedServiceLines.find(
        (item) => item.id === id,
      );

      if (alreadyFollowed) {
        return; // Item already followed, skip processing
      }

      // Find and remove the item from recommendedServiceLines
      const recommendedIndex = state.recommendedServiceLines.findIndex(
        (item) => item.id === id,
      );

      if (recommendedIndex !== -1) {
        // Remove from recommended list
        state.recommendedServiceLines.splice(recommendedIndex, 1);

        // Add to followed list
        const serviceline = { ...action.payload };
        serviceline.added = true;
        serviceline.loading = false;
        state.followedServiceLines.unshift(serviceline);

        state.serviceLinesEmpty = false;
      }
    },
    addedServiceLineError(state, action: PayloadAction<ServiceLine>) {
      const { id } = action.payload;

      // Reset loading state for the specific item in recommended list
      const recommendedIndex = state.recommendedServiceLines.findIndex(
        (item) => item.id === id,
      );
      if (recommendedIndex >= 0) {
        state.recommendedServiceLines[recommendedIndex].loading = false;
      }

      // Reset global follow loading state
      if (state.followLoading) {
        state.followLoading = false;
      }
    },
    getServiceLineWidgets(state, action: PayloadAction<SLNewsRequest>) {
      state.widgetLoading = true;
      state.servicelineName = '';
      state.servicelineAdded = false;
      state.serviceLineWidgets = [];
      state.pendingWidgetDetails = null;
    },
    setServiceLineWidgets(state, action: PayloadAction<SLWidgetResponse>) {
      const { payload } = action;
      const isKPMG = payload.kpmgUser === true;
      state.widgetLoading = false;
      state.servicelineName = payload?.name;
      state.servicelineAdded = payload?.subscribed;
      state.serviceLineWidgets = payload?.widgets?.map(
        ((widget) => ({
          ...widget,
          ...(!isKPMG && { news: widget.news }),
          page: 1,
          ...(widget.filters && { newsFilters: mergeFilters(widget.filters || []) }),
          ...(widget.filters && { selectedFilters: [] }),
          customNewsFilter: mergeFilters(widget.customFilters),
          articleEnded: false,
        })),
      ) || [];

      // Fix: If we have a pending widget details response (from when widgets weren't loaded),
      // apply it now that widgets are loaded
      if (state.pendingWidgetDetails && state.serviceLineWidgets.length > 0) {
        const widtget = state.serviceLineWidgets.find(
          (widget) => widget.title === state.pendingWidgetDetails?.title,
        );
        if (widtget) {
          // Use current isKPMG state (from payload) instead of pending details kpmgUser
          // This ensures we use the correct KPMG state even if pending details have undefined kpmgUser
          if (widtget.page === 1) {
            state.newsArticlesLoading = false;
            if (isKPMG) {
              // For KPMG users, always use articles from details API, never from widget API
              widtget.news = state.pendingWidgetDetails.articles || [];
              widtget.customNewsFilter = widtget.customNewsFilter.length ? widtget.customNewsFilter : mergeFilters(state.pendingWidgetDetails?.customFilters || []);
            } else {
              const widgetNews = widtget.news || [];
              const concatedArticles = widgetNews.concat(state.pendingWidgetDetails?.articles);
              widtget.news = concatedArticles.filter(
                (value, index, self) => index === self.findIndex((t) => (
                  t.articleId === value.articleId
                )),
              );
              widtget.newsFilters = widtget.newsFilters.length ? widtget.newsFilters
                : mergeFilters(state.pendingWidgetDetails?.filters || []);
            }
          } else {
            if (isKPMG) {
              const existingNews = widtget.news || [];
              widtget.news = existingNews.concat(state.pendingWidgetDetails?.articles);
            } else {
              widtget.news = (widtget.news || []).concat(state.pendingWidgetDetails?.articles);
            }
          }
          widtget.page += 1;
          state.detailLoading = false;
          state.paginationLoader = false;
          state.newsArticlesLoading = false;
        } else {
          state.detailLoading = false;
          state.paginationLoader = false;
          state.newsArticlesLoading = false;
        }
        state.pendingWidgetDetails = null;
      } else if ((state.detailLoading || state.newsArticlesLoading) && state.serviceLineWidgets.length > 0) {
        state.detailLoading = false;
        state.newsArticlesLoading = false;
        state.paginationLoader = false;
      }
    },
    getServiceLineDetails(state, action: PayloadAction<SLNewsRequest>) {
      const { payload } = action;
      const AppliedFilters = payload?.filters?.filter(
        (item) => item.isSelected,
      ).map((item) => ({
        id: item.filterId,
        type: item.filterTypeName,
      }));
      state.detailLoading = true;
      // Clear pending details when a new request starts
      if (payload.page === 1) {
        state.pendingWidgetDetails = null;
      }
      if (payload.page === 1) {
        state.newsArticles = [];
        if (payload.widget_name) {
          const widtget = state.serviceLineWidgets?.find(
            (widget) => widget.title === payload.widget_name,
          );
          if (widtget) {
            widtget.articleEnded = false;
            if (!payload.kpmgMode) {
              widtget.news = widtget.news?.length && !AppliedFilters?.length
                ? widtget.news : [];
            } else {
              widtget.news = [];
            }
            widtget.page = 1;
            if (!payload.filters) {
              widtget.newsFilters = widtget.newsFilters?.length ? widtget.newsFilters : [];
              widtget.selectedFilters = [];
            } else {
              widtget.selectedFilters = payload.filters;
            }
          }
          state.newsArticlesLoading = true;
          state.paginationLoader = true;
        } else {
          state.serviceLineWidgets = [];
          state.articleNewsEnded = false;
          state.servicelineName = '';
          state.servicelineAdded = false;
          state.newsArticlesLoading = true;
          state.newsTabPage = 1;
          if (!payload.filters) {
            state.newsFilter = [];
            state.selectedNewsFilter = [];
          } else {
            state.selectedNewsFilter = payload.filters;
          }
        }
      } else {
        state.paginationLoader = true;
      }
    },
    setWidgetNewsDetails(state, action: PayloadAction<SLDetailResponse>) {
      const { payload } = action;
      const widtget = state.serviceLineWidgets?.find(
        (widget) => widget.title === payload.title,
      );
      if (widtget) {
        const kpmgUser = payload.kpmgUser === true;
        if (widtget.page === 1) {
          state.newsArticlesLoading = false;
          if (kpmgUser) {
            widtget.news = payload.articles || [];
            if (widtget.page === 1 && payload?.customFilters) {
              widtget.customNewsFilter = mergeFilters(payload.customFilters);
            }
          } else {
            const widgetNews = widtget.news || [];
            const concatedArticles = widgetNews.concat(payload?.articles);
            widtget.news = concatedArticles.filter(
              (value, index, self) => index === self.findIndex((t) => (
                t.articleId === value.articleId
              )),
            );
            widtget.newsFilters = widtget.newsFilters.length ? widtget.newsFilters
              : mergeFilters(payload?.filters || []);
          }

        } else {
          if (kpmgUser) {
            const existingNews = widtget.news || [];
            widtget.news = existingNews.concat(payload?.articles);
          } else {
            widtget.news = (widtget.news || []).concat(payload?.articles);
          }
        }
        widtget.page += 1;
        state.detailLoading = false;
        state.paginationLoader = false;
        state.newsArticlesLoading = false;
        state.pendingWidgetDetails = null;
      } else {
        if (!state.widgetLoading &&
          state.serviceLineWidgets && state.serviceLineWidgets.length > 0) {
          state.detailLoading = false;
          state.paginationLoader = false;
          state.newsArticlesLoading = false;
          state.pendingWidgetDetails = null;
        } else {
          // Widgets still loading - store the response to apply when widgets finish loading
          // Note: This only happens for page 1 requests (page > 1 requires widgets to exist)
          // When widgets are created, they start at page 1, so pending details are always page 1
          state.pendingWidgetDetails = payload;
        }
      }
    },
    setWidgetArticlesEnded(state, action: PayloadAction<string>) {
      const { payload } = action;
      const widtget = state.serviceLineWidgets?.find(
        (widget) => widget.title === payload,
      );
      if (widtget) {
        widtget.articleEnded = true;
      }
    },
    setNewsDetails(state, action: PayloadAction<SLDetailResponse>) {
      const { payload } = action;
      state.detailLoading = false;
      if (state.newsTabPage === 1) {
        state.newsArticlesLoading = false;
        state.servicelineName = payload?.name;
        state.servicelineAdded = payload?.subscribed;
        // Deduplication logic for unique articles based on articleId (first page)
        const incomingArticles = payload?.articles || [];

        const incomingSeen = new Set<number>();
        const dedupedIncoming = incomingArticles.filter(article => {
          if (!article.articleId || incomingSeen.has(article.articleId)) return false;
          incomingSeen.add(article.articleId);
          return true;
        });

        state.newsArticles = dedupedIncoming;
        state.newsFilter = state.newsFilter.length ? state.newsFilter
          : mergeFilters(payload?.filters || []);
      } else {
        // Deduplication logic for unique articles based on articleId
        const incomingArticles = payload?.articles || [];

        const incomingSeen = new Set<number>();
        const dedupedIncoming = incomingArticles.filter(article => {
          if (!article.articleId || incomingSeen.has(article.articleId)) return false;
          incomingSeen.add(article.articleId);
          return true;
        });

        const existingIds = new Set(state.newsArticles.map(article => article.articleId));
        const finalUnique = dedupedIncoming.filter(
          article => !existingIds.has(article.articleId)
        );

        state.newsArticles = [...state.newsArticles, ...finalUnique];
      }
      state.newsTabPage += 1;
      state.paginationLoader = false;
    },
    setNewsArticlesEnded(state) {
      state.articleNewsEnded = true;
    },
    addToFavoriteSuccess(state, action: PayloadAction<AddToFavoriteRequest>) {
      const { articleId, favoriteFlag } = action.payload;
      if (articleId) {
        if (state.newsArticles && state.newsArticles.length) {
          const matchedArticles = state.newsArticles.filter(
            (article) => article.articleId === articleId,
          );
          if (matchedArticles.length > 0) {
            matchedArticles.forEach((arrayItem) => {
              const article = arrayItem;
              article.isFavorite = favoriteFlag === 1;
            });
          }
        }
        if (state.serviceLineWidgets && state.serviceLineWidgets.length) {
          state.serviceLineWidgets.forEach((widget) => {
            const matchedArticles = widget.news.filter(
              (article) => article.articleId === articleId,
            );
            if (matchedArticles?.length > 0) {
              matchedArticles.forEach((arrayItem) => {
                const article = arrayItem;
                article.isFavorite = favoriteFlag === 1;
              });
            }
          });
        }
      }
    },
    resetFollowLoading(state) {
      state.followLoading = false;
    },
    setFilters(state, action: PayloadAction<SRIFiltersRequest>) {
      const { payload } = action;
      const startDate = payload.start_date;
      const endDate = payload.end_date;
      const { allTime } = payload;
      state.filters = { start_date: startDate, end_date: endDate, allTime };
      state.filtersType = payload.type;
    },
    getSLCompanies(state, action: PayloadAction<GetCompaniesRequest>) {
      state.companiesLoading = true;
      // Reset companies array when offset is 0 (initial load)
      if (action.payload.offset === 0) {
        state.companies = [];
      }
    },
    setSLCompanies(state, action: PayloadAction<CompaniesDataResponse>) {
      const { response } = action.payload;
      const { companies, companiesCount } = response;
      state.companiesCount = companiesCount ?? 0;
      if (companies?.length) {
        state.companies = [...state.companies, ...companies];
      }
      state.companiesLoading = false;
    },
    editCompanyInServiceLine(state, action: PayloadAction<SLEditCompanyRequest>) {
      state.companyAdded = false;
    },
    setAddCompanyInServiceLine(state) {
      state.companyAdded = true;
    },
    refreshAfterAddCompany(state, action: PayloadAction<{ serviceLineId: number, widgetName: string, dateRangeFilter: SRIFiltersRequest, regions: string }>) {
    },
    getRegions(state, action: PayloadAction<{ serviceLineId: number }>) {
      state.regionsLoading = true;
    },
    setRegions(state, action: PayloadAction<RegionsResponse>) {
      state.regions = action.payload.regions;
    },
    setSelectedRegions(state, action: PayloadAction<string[]>) {
      state.selectedRegions = action.payload;
    },
  },
});

export const { actions, reducer, name: sliceKey } = ServiceLinesSlice;